import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';

// GuardPanel — on-screen move controls for the concert-hall security guard
// (?guard=edit). Nudge him around the entrance (offset from the building
// anchor), rotate, size — live. Copy the values to bake into worldStore.guardTune
// defaults. Same feel as the resident placement pad.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 42,
  width: 196, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '9px 0', cursor: 'pointer',
  font: '600 14px system-ui', textAlign: 'center', userSelect: 'none',
};
const lab = { fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7, margin: '8px 0 4px' };

export default function GuardPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'guard';
  const g = useWorldStore((s) => s.guardTune);
  const set = useWorldStore((s) => s.setGuardTune);
  const [fine, setFine] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!ON) return null;

  const step = fine ? 0.25 : 1;
  const r = (v) => Math.round(v * 100) / 100;
  const move = (dx, dz) => set({ x: r(g.x + dx * step), z: r(g.z + dz * step) });
  const raise = (dir) => set({ y: r(g.y + dir * (fine ? 0.05 : 0.2)) });
  const copy = () => {
    navigator.clipboard?.writeText(`x: ${r(g.x)}, y: ${r(g.y)}, z: ${r(g.z)}, rot: ${r(g.rot)}, h: ${r(g.h)}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {});
  };

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7 }}>Guard</div>
      <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11.5, opacity: 0.85, margin: '4px 0 6px' }}>
        x {r(g.x)} · y {r(g.y)} · z {r(g.z)} · rot {r(g.rot)} · h {r(g.h)}
      </div>

      <div style={lab}>Move</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
        <span />
        <button style={btn} onClick={() => move(0, -1)}>↑</button>
        <span />
        <button style={btn} onClick={() => move(-1, 0)}>←</button>
        <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>x/z</div>
        <button style={btn} onClick={() => move(1, 0)}>→</button>
        <span />
        <button style={btn} onClick={() => move(0, 1)}>↓</button>
        <span />
      </div>

      <div style={lab}>Raise / lower</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => raise(-1)}>▼ down</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => raise(1)}>up ▲</button>
      </div>

      <div style={lab}>Turn</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ rot: r(g.rot - (fine ? 0.1 : Math.PI / 2)) })}>⟲</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ rot: r(g.rot + (fine ? 0.1 : Math.PI / 2)) })}>⟳</button>
      </div>

      <div style={lab}>Size</div>
      <div style={{ display: 'flex', gap: 5 }}>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ h: Math.max(0.5, r(g.h - (fine ? 0.02 : 0.1))) })}>−</button>
        <button style={{ ...btn, flex: 1 }} onClick={() => set({ h: r(g.h + (fine ? 0.02 : 0.1)) })}>+</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '10px 0', cursor: 'pointer', opacity: 0.9 }}>
        <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
        fine steps
      </label>
      <button style={{ ...btn, width: '100%', background: copied ? '#9dff7d' : 'rgba(255,178,94,0.14)', color: copied ? '#0c2c06' : '#ffd27d', fontSize: 12 }} onClick={copy}>
        {copied ? '✓ copied' : 'copy values'}
      </button>
    </div>
  );
}
